import React__default, { forwardRef } from 'react';
import { SubTitle, Body, Caption } from '../Typography/index.es.js';
import { Shape } from '../Shape/index.es.js';
import { DeleteCircleColored, CautionOutlined } from '@teko/ds-icon';
import { CommonStyles } from '../BaseProps/index.es.js';
import { MAPPING_SIZE } from '../../constants/index.es.js';
import { SizeContext } from '../../contexts/SizeContext.es.js';
import styled from '@emotion/styled';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Input = forwardRef((_a, ref) => {
  var _b = _a, {
    title,
    value,
    defaultValue,
    autoFocus,
    type = "text",
    placeholder,
    caption,
    showCaptionIcon = true,
    error,
    disabled,
    readonly,
    prefix,
    suffix,
    maxLength = 255,
    className,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    size: customizeSize,
    id,
    allowClear = false
  } = _b, rest = __objRest(_b, [
    "title",
    "value",
    "defaultValue",
    "autoFocus",
    "type",
    "placeholder",
    "caption",
    "showCaptionIcon",
    "error",
    "disabled",
    "readonly",
    "prefix",
    "suffix",
    "maxLength",
    "className",
    "onChange",
    "onKeyDown",
    "onFocus",
    "onBlur",
    "size",
    "id",
    "allowClear"
  ]);
  var _a2, _b2;
  const size = React__default.useContext(SizeContext);
  const mergeSize = customizeSize || size || "m";
  const handleFocus = (e) => {
    onFocus && onFocus(e);
  };
  const handleBlur = (e) => {
    onBlur && onBlur(e);
  };
  const colorState = disabled ? "disable" : error ? "error500" : void 0;
  const handleClear = () => {
    onChange == null ? void 0 : onChange("");
  };
  return /* @__PURE__ */ React__default.createElement(StyledInput, __spreadValues({
    className
  }, rest), title && /* @__PURE__ */ React__default.createElement(SubTitle, {
    level: 4,
    color: "textPrimary",
    marginBottom: "s4"
  }, title), /* @__PURE__ */ React__default.createElement(InputContainer, {
    className: "input-container",
    padding: ["s0", "s12"],
    borderWeight: "m",
    borderRadius: "s",
    borderStyle: "solid",
    height: MAPPING_SIZE[mergeSize],
    colorState: colorState || "primary500",
    readonly: !!readonly,
    disabled: !!disabled
  }, prefix && /* @__PURE__ */ React__default.createElement(StyledSpace, {
    height: MAPPING_SIZE[mergeSize],
    marginRight: "s4"
  }, typeof prefix === "string" ? /* @__PURE__ */ React__default.createElement(Body, {
    level: 2,
    color: disabled ? "border" : "textPrimary"
  }, prefix) : React__default.cloneElement(prefix, {
    size: ((_a2 = prefix == null ? void 0 : prefix.props) == null ? void 0 : _a2.size) || 20
  })), /* @__PURE__ */ React__default.createElement(StyledBaseInput, {
    ref,
    id,
    type,
    value,
    defaultValue,
    autoFocus,
    placeholder,
    onChange: (event) => onChange && onChange(event.target.value),
    onFocus: handleFocus,
    onBlur: handleBlur,
    maxLength,
    disabled,
    readOnly: readonly,
    backgroundColor: rest.backgroundColor || "",
    onKeyDown: (event) => {
      if (onKeyDown)
        onKeyDown(event);
    }
  }), allowClear && !!value && /* @__PURE__ */ React__default.createElement(DeleteCircleColored, {
    name: "circle-close",
    onClick: handleClear,
    size: 20,
    cursor: "pointer"
  }), suffix && /* @__PURE__ */ React__default.createElement(StyledSpace, {
    height: MAPPING_SIZE[mergeSize],
    marginLeft: "s4"
  }, typeof suffix === "string" ? /* @__PURE__ */ React__default.createElement(Body, {
    level: 2,
    color: disabled ? "border" : "textPrimary"
  }, suffix) : React__default.cloneElement(suffix, {
    size: ((_b2 = suffix == null ? void 0 : suffix.props) == null ? void 0 : _b2.size) || 20
  }))), caption && /* @__PURE__ */ React__default.createElement(StyledSpace, {
    marginTop: "s8"
  }, showCaptionIcon && /* @__PURE__ */ React__default.createElement(CautionOutlined, {
    size: 16,
    color: colorState || "textSecondary"
  }), /* @__PURE__ */ React__default.createElement(Caption, {
    level: 1,
    color: colorState || "textSecondary",
    marginLeft: "s4"
  }, caption)));
});
Input.displayName = "Input";
const StyledInput = styled.div`
  ${CommonStyles}
`;
const StyledBaseInput = styled.input`
  flex-grow: 1;
  background-color: ${(props) => props.backgroundColor ? props.backgroundColor + " !important" : "inherit"};
`;
const StyledSpace = styled(Shape)`
  display: flex;
  align-items: center;
`;
const InputContainer = styled(Shape)`
  display: flex;
  align-items: center;
  border-color: ${(p) => p.disabled ? p.theme.colors.background : p.theme.colors.inputBorder};
  gap: 0.25rem;

  :focus,
  :focus-within {
    border-color: ${(p) => p.theme.colors[p.colorState]};
  }

  ${(p) => p.disabled || p.readonly ? `background-color: ${p.theme.colors.background};` : p.theme.colors.white}
  overflow: hidden;
  input {
    flex: 1;
    width: 100%;
    border: none;
    font-size: 13px;
    color: ${(p) => p.disabled ? p.theme.colors.border : p.theme.colors.textPrimary};

    &::placeholder {
      color: ${(p) => p.disabled ? p.theme.colors.disable : p.theme.colors.placeholder};
    }
    &:focus {
      outline: none;
    }
    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

export { Input };
